import React, { useEffect, useState } from "react";
import InputField from "../../SharedComponent/Fields/InputField";
import { useTranslation } from "react-i18next";
import SelectField from "../../SharedComponent/Fields/SelectField";
import TextAreaFielD from "../../SharedComponent/Fields/TextareaField";
import { ImagePlus, Upload, X } from "lucide-react";
import { getAttachmentLargeUrl, getAttachmentThumbUrl, getAttachmentDocumentUrl } from "../../utils/attachmentImages";

const SpareParts = ({
  formatAttachment,
  handleDeleteAttachment,
  assetAttachments,
  newAsset,
  handleChange,
  warehouses,
  selectedWarehouse,
  handleWarehouseChange,
  warehouseLocationId,
  selectedFiles,
  handleRemoveFile,
  handleFileChange,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {/* Basics Section */}
      <div className="bg-white p-4 shadow-md">
        <h2 className="text-xl font-medium mb-3 text-gray-800">
          {t("assetPageHeadings.basics")}
        </h2>

        {/* Row 1: Classification, Manufacturer, Type, Serial No */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <SelectField
            name="sparetype"
            required
            value={newAsset.sparetype}
            onChange={handleChange}
            label={t("assets.fields.classification")}
            explanation={t("assets.fields.explanation.classification")}
            placeholder={t("assets.fields.selectClassification")}
            menus={[
              { value: "", option: t("spareParts.classificationTypes.select") },
              {
                value: "batteryindicator",
                option: t("spareParts.classificationTypes.batteryindicator"),
              },
              {
                value: "board",
                option: t("spareParts.classificationTypes.board"),
              },
              {
                value: "cable",
                option: t("spareParts.classificationTypes.cable"),
              },
              { value: "cap", option: t("spareParts.classificationTypes.cap") },
              {
                value: "chassispart",
                option: t("spareParts.classificationTypes.chassispart"),
              },
              {
                value: "control",
                option: t("spareParts.classificationTypes.control"),
              },
              {
                value: "cover",
                option: t("spareParts.classificationTypes.cover"),
              },
              {
                value: "cylinder",
                option: t("spareParts.classificationTypes.cylinder"),
              },
              {
                value: "driveengine",
                option: t("spareParts.classificationTypes.driveengine"),
              },
              {
                value: "drivetrain",
                option: t("spareParts.classificationTypes.drivetrain"),
              },
              {
                value: "harness",
                option: t("spareParts.classificationTypes.harness"),
              },
              {
                value: "hydraulics",
                option: t("spareParts.classificationTypes.hydraulics"),
              },
              {
                value: "magneticbrake",
                option: t("spareParts.classificationTypes.magneticbrake"),
              },
              {
                value: "motorcontrol",
                option: t("spareParts.classificationTypes.motorcontrol"),
              },
              {
                value: "others",
                option: t("spareParts.classificationTypes.others"),
              },
              {
                value: "powertrain",
                option: t("spareParts.classificationTypes.powertrain"),
              },
              {
                value: "pumpengine",
                option: t("spareParts.classificationTypes.pumpengine"),
              },
              {
                value: "relay",
                option: t("spareParts.classificationTypes.relay"),
              },
              {
                value: "seat",
                option: t("spareParts.classificationTypes.seat"),
              },
              {
                value: "steeringcolumn",
                option: t("spareParts.classificationTypes.steeringcolumn"),
              },
              {
                value: "steeringmotor",
                option: t("spareParts.classificationTypes.steeringmotor"),
              },
              {
                value: "supportwheel",
                option: t("spareParts.classificationTypes.supportwheel"),
              },
              {
                value: "tube",
                option: t("spareParts.classificationTypes.tube"),
              },
              {
                value: "tyres",
                option: t("spareParts.classificationTypes.tyres"),
              },
              {
                value: "valveblock",
                option: t("spareParts.classificationTypes.valveblock"),
              },
            ]}
            selectClassName={
              newAsset.sparetype === ""
                ? "text-gray-400 font-light"
                : "text-gray-900 font-normal"
            }
          />
          <InputField
            name="manufacturer"
            required
            value={newAsset.manufacturer}
            onChange={handleChange}
            label={t("assets.fields.manufacturer")}
            explanation={t("assets.fields.explanation.manufacturer")}
            placeholder={t("createAsset.placeholder.enterManufacturer")}
            maxLength={100}
          />

          <InputField
            name="type"
            value={newAsset.type}
            onChange={handleChange}
            label={t("assets.fields.type")}
            placeholder={t("createAsset.placeholder.enterType")}
            explanation={t("assets.fields.explanation.type")}
            maxLength={30}
          />
          <InputField
            name="sparepart_id"
            value={newAsset.sparepart_id}
            onChange={handleChange}
            label={t("spareParts.fields.sparepartId")}
            placeholder={t("spareParts.placeholders.sparepartId")}
            explanation={t("spareParts.explanations.sparepartId")}
            maxLength={20}
          />
        </div>

        {/* Row 2: Internal No, Status, Engine Type, Basket Load */}
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mt-2">
          <InputField
            name="serial_no"
            value={newAsset.serial_no}
            onChange={handleChange}
            placeholder={t("createAsset.placeholder.enterSerialNumber")}
            explanation={t("assets.fields.serialNumber")}
            label={t("assets.fields.machineSerialNo")}
            maxLength={100}
          />
          <InputField
            name="sparepart_internalno"
            value={newAsset.sparepart_internalno}
            onChange={handleChange}
            maxLength={100}
            label={t("createAsset.placeholders.internNoLabel")}
            explanation={t("createAsset.placeholders.internNoExplanation")}
            placeholder={t("createAsset.placeholders.internNoPlaceholder")}
          />
          <InputField
            name="warehouse_number"
            value={newAsset.warehouse_number}
            onChange={handleChange}
            label={t("spareParts.fields.warehouseNumber")}
            explanation={t("spareParts.explanations.warehouseNumber")}
            placeholder={t("spareParts.placeholders.warehouseNumber")}
            maxLength={10}
          />

          <SelectField
            name="status"
            value={newAsset.status}
            onChange={handleChange}
            label={t("assets.fields.status")}
            placeholder={t("createAsset.placeholder.enterStatus")}
            explanation={t("assets.fields.explanation.status")}
            menus={[
              { value: "", option: t("assets.fields.selectStatus") },
              { value: "New", option: t("assets.options.new") },
              { value: "Used", option: t("assets.options.used") },
              { value: "Demo", option: t("assets.options.demo") },
            ]}
          />
          <div>
            <InputField
              name="year_of_manufacture"
              value={newAsset.year_of_manufacture || ""}
              onChange={handleChange}
              label={t("assets.fields.yearOfManufacture")}
              explanation={t("assets.fields.explanation.yearOfManufacture")}
              placeholder={t("createAsset.placeholder.enterYearOfManufacture")}
              type="year"
              maxLength={100}
            />
          </div>
        </div>

        {/* Row 5: Available From */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mt-2">
          <SelectField
            name="technical_condition"
            value={newAsset.technical_condition}
            onChange={handleChange}
            label={t("createAsset.placeholders.technicalConditionLabel")}
            explanation={t(
              "createAsset.placeholders.technicalConditionExplanation"
            )}
            placeholder={t(
              "createAsset.placeholders.technicalConditionPlaceholder"
            )}
            menus={[
              {
                value: "",
                option: t(
                  "createAsset.placeholders.technicalConditionPlaceholder"
                ),
              },
              {
                value: 2,
                option: t("assets.technicalCondition.very_good"),
              },
              {
                value: 3,
                option: t("assets.technicalCondition.good"),
              },
              {
                value: 4,
                option: t("assets.technicalCondition.used"),
              },
              {
                value: 5,
                option: t("assets.technicalCondition.bad"),
              },
              {
                value: 6,
                option: t("assets.technicalCondition.very_bad"),
              },
              {
                value: 7,
                option: t("assets.technicalCondition.new"),
              },
            ]}
          />
          <SelectField
            name="optical_condition"
            value={newAsset.optical_condition}
            onChange={handleChange}
            label={t("createAsset.placeholders.opticalConditionLabel")}
            explanation={t(
              "createAsset.placeholders.opticalConditionExplanation"
            )}
            placeholder={t(
              "createAsset.placeholders.opticalConditionPlaceholder"
            )}
            menus={[
              {
                value: "",
                option: t(
                  "createAsset.placeholders.opticalConditionPlaceholder"
                ),
              },
              {
                value: 2,
                option: t("assets.opticalCondition.very_good"),
              },
              { value: 3, option: t("assets.opticalCondition.good") },
              { value: 4, option: t("assets.opticalCondition.used") },
              { value: 5, option: t("assets.opticalCondition.bad") },
              { value: 6, option: t("assets.opticalCondition.very_bad") },
              { value: 7, option: t("assets.opticalCondition.new") },
            ]}
          />
          <div>
            <InputField
              name="operating_hours"
              type="number"
              value={newAsset.operating_hours}
              onChange={handleChange}
              label={t("assets.fields.operatingHours")}
              explanation={t("assets.fields.explanation.operatingHours")}
              placeholder={t("cleaningEquipment.placeholders.operatingHours")}
              maxLength={8}
            />
          </div>
          <InputField
            name="sparepart_available"
            type="date"
            value={newAsset.sparepart_available}
            onChange={handleChange}
            label={t("spareParts.fields.availableFrom")}
            placeholder={t("spareParts.placeholders.availableFrom")}
            explanation={t("spareParts.explanations.availableFrom")}
          />
        </div>

        {/* Row 6: Warehouse + Location */}
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mt-2">
          {Array.isArray(warehouses) && warehouses?.length > 0 && (
            <SelectField
              name="warehouse"
              value={selectedWarehouse?.id || warehouseLocationId || ""}
              onChange={handleWarehouseChange}
              label="Warehouse"
              menus={[
                { value: "", option: "Select Warehouse" },
                ...warehouses.map((w) => ({ value: w.id, option: w.name })),
              ]}
            />
          )}

          <div>
            <InputField
              required
              name="dealer_street"
              value={newAsset.dealer_street}
              onChange={handleChange}
              maxLength={20}
              label={t("createAsset.placeholders.street")}
              placeholder={t("createAsset.placeholders.streetPlaceholder")}
              explanation={t("createAsset.placeholders.streetExplanation")}
            />
          </div>

          <div>
            <InputField
              required
              name="dealer_city"
              value={newAsset.dealer_city}
              onChange={handleChange}
              label={t("createAsset.placeholders.city")}
              explanation={t("createAsset.placeholders.cityExplanation")}
              placeholder={t("createAsset.placeholders.cityPlaceholder")}
              maxLength={50}
            />
          </div>

          <div>
            <InputField
              required
              name="dealer_country"
              value={newAsset.dealer_country}
              onChange={handleChange}
              label={t("createAsset.placeholders.country")}
              explanation={t("createAsset.placeholders.countryExplanation")}
              placeholder={t("createAsset.placeholders.countryPlaceholder")}
              maxLength={50}
            />
          </div>

          <div>
            <InputField
              required
              name="dealer_zip"
              value={newAsset.dealer_zip}
              onChange={handleChange}
              type="number"
              label={t("createAsset.placeholders.zipCode")}
              explanation={t("createAsset.placeholders.zipCodePlaceholder")}
              placeholder={t("createAsset.placeholders.zipCodeExplanation")}
              maxLength={7}
            />
          </div>
        </div>

        {/* Row 7: Additional Description */}
        <div className="mt-2">
          <TextAreaFielD
            name="description"
            value={newAsset.description}
            onChange={handleChange}
            label={t("createAsset.placeholders.additionalDescription")}
            placeholder={t(
              "createAsset.placeholders.additionalDescriptionPlaceholder"
            )}
          />
        </div>
      </div>

      {/* Media Upload Section */}
      <div className="bg-white p-4 shadow-md mt-4">
        <h2 className="text-xl font-medium mb-3 text-gray-800">
          {t("assetPageHeadings.media")}
        </h2>

        {/* Selected Files */}
        {selectedFiles?.length > 0 && (
          <div className="space-y-2 mt-2">
            <h6>Selected Media</h6>
            <ul className="space-y-1 flex flex-col gap-3">
              {Array.from(selectedFiles).map((file, index) => (
                <li key={index} className="flex items-center gap-4">
                  {file.type.startsWith("image/") && (
                    <img
                      className="h-10 w-10"
                      alt={`workplatform ${index}`}
                      src={URL.createObjectURL(file)}
                    />
                  )}
                  <a
                    href={URL.createObjectURL(file)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 text-sm font-semibold underline ml-2"
                  >
                    {file.name}
                  </a>
                  <div
                    onClick={() => handleRemoveFile(index)}
                    className="cursor-pointer"
                  >
                    <X className="h-5 w-5" />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Uploaded Media */}
        {assetAttachments?.length > 0 && (
          <div className="flex flex-col gap-2 mt-4">
            <h6>Uploaded Media</h6>
            <div className="flex flex-col gap-3">
              {assetAttachments.map((attachment, index) => (
                <div key={index} className="flex items-center gap-2">
                  {attachment?.image ? (
                    <>
                      <img
                        className="h-10 w-10"
                        src={getAttachmentThumbUrl(attachment)}
                        alt="workplatform"
                      />
                      <a
                        href={getAttachmentLargeUrl(attachment)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 text-sm font-semibold underline ml-2"
                      >
                        {formatAttachment(getAttachmentLargeUrl(attachment))}
                      </a>
                    </>
                  ) : (
                    <a
                      href={getAttachmentDocumentUrl(attachment)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 text-sm font-semibold underline ml-2"
                    >
                      {formatAttachment(getAttachmentDocumentUrl(attachment))}
                    </a>
                  )}
                  <div
                    onClick={() => handleDeleteAttachment(attachment?.id)}
                    className="cursor-pointer"
                  >
                    <X className="h-5 w-5" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* File Upload */}
        <div className="my-4">
          <div className="relative">
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              accept="image/*, .xlsx, .xls, .doc, .docx, .pdf"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              id="workplatform-upload"
            />
            <label
              htmlFor="workplatform-upload"
              className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 hover:border-gray-400"
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <div className="flex items-center space-x-2 mb-2">
                  <Upload className="w-6 h-6 text-gray-400" />
                  <ImagePlus className="w-6 h-6 text-gray-400" />
                </div>
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">
                    {t("assetPageHeadings.clickToUpload")}
                  </span>
                </p>
                <p className="text-xs text-gray-400">
                  {t("assetPageHeadings.fileSupported")}
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>
    </>
  );
};

export default SpareParts;
